import { OrthographicCamera, Vector2, Vector3 } from 'three';
import { BASE_PPT, type RenderSize } from '../size';

const DISTANCE = 60;

/**
 * The fixed stage camera (05 §3): orthographic, square-on to the playfield (no yaw), tilted
 * down by `pitchDeg` so the tops of the blocks show, which is what makes the stage a diorama.
 * World units are tiles: +x right, +y up, +z toward the viewer. The playfield itself is the
 * plane z = 0; the tilt never changes where anything is in the game, only how it is seen.
 *
 * Its position is snapped to the texel grid, so static geometry always lands on the same
 * pixels; the remainder slides the upscaled image by less than a texel (05 §8.2). That only
 * matters while the camera moves: in the drop between two levels.
 */
export class StageCamera {
  readonly camera: OrthographicCamera;
  /** World point that should sit at the centre of the screen. */
  readonly focus = new Vector3();
  /** Remainder of the snap, in texels, for the upscale pass. Each component is in [-0.5, 0.5]. */
  readonly subTexel = new Vector2();
  /** Integer texel coordinates of the snapped camera: anchors dither patterns to the world. */
  readonly camTexel = new Vector2();
  readonly sinPitch: number;
  readonly cosPitch: number;
  private readonly forward: Vector3;
  private readonly up: Vector3;
  private ppt: number;

  /**
   * `size` is the internal resolution; `pixelsPerTile` texels across one 8-pixel tile of the
   * original (default: 14 at the base 640x360, scaled with it); `pitchDeg` 0 = square-on.
   */
  constructor(readonly size: RenderSize, pixelsPerTile = BASE_PPT * size.scale, pitchDeg = 18) {
    const pitch = (pitchDeg * Math.PI) / 180;
    this.sinPitch = Math.sin(pitch);
    this.cosPitch = Math.cos(pitch);
    this.forward = new Vector3(0, -this.sinPitch, -this.cosPitch);
    this.up = new Vector3(0, this.cosPitch, -this.sinPitch);
    this.ppt = pixelsPerTile;
    this.camera = new OrthographicCamera(-1, 1, 1, -1, 1, DISTANCE * 2);
    this.applyFrustum();
    this.update();
  }

  get pixelsPerTile(): number {
    return this.ppt;
  }

  set pixelsPerTile(value: number) {
    this.ppt = value;
    this.applyFrustum();
  }

  private applyFrustum(): void {
    const halfW = this.size.targetW / 2 / this.ppt;
    const halfH = this.size.targetH / 2 / this.ppt;
    this.camera.left = -halfW;
    this.camera.right = halfW;
    this.camera.top = halfH;
    this.camera.bottom = -halfH;
    this.camera.updateProjectionMatrix();
  }

  /** Camera-plane coordinates (tiles along screen right / screen up) of a world point. */
  toPlane(p: Vector3): { x: number; y: number } {
    return { x: p.x, y: p.y * this.up.y + p.z * this.up.z };
  }

  update(): void {
    const plane = this.toPlane(this.focus);
    const sx = Math.round(plane.x * this.ppt) / this.ppt;
    const sy = Math.round(plane.y * this.ppt) / this.ppt;
    this.subTexel.set((plane.x - sx) * this.ppt, (plane.y - sy) * this.ppt);
    this.camTexel.set(Math.round(sx * this.ppt), Math.round(sy * this.ppt));

    const dy = sy - plane.y;
    const fx = sx;
    const fy = this.focus.y + this.up.y * dy;
    const fz = this.focus.z + this.up.z * dy;
    this.camera.position.set(fx - this.forward.x * DISTANCE, fy - this.forward.y * DISTANCE, fz - this.forward.z * DISTANCE);
    this.camera.lookAt(fx, fy, fz);
    this.camera.updateMatrixWorld();
  }

  /**
   * Texel lock (05 §6.2): nudge a world position so it projects onto a whole texel. Moves
   * along x and y only, so a sprite never leaves the playfield plane. Returns `out`.
   */
  snapToTexel(p: Vector3, out: Vector3): Vector3 {
    const plane = this.toPlane(p);
    const dx = Math.round(plane.x * this.ppt) / this.ppt - plane.x;
    const dy = Math.round(plane.y * this.ppt) / this.ppt - plane.y;
    return out.set(p.x + dx, p.y + dy / this.up.y, p.z);
  }

  /** Where a world point lands in the visible image, texels from its top-left corner. */
  worldToScreen(p: Vector3): { x: number; y: number } {
    const plane = this.toPlane(p);
    return {
      x: plane.x * this.ppt - this.camTexel.x - this.subTexel.x + this.size.viewW / 2,
      y: this.size.viewH / 2 - (plane.y * this.ppt - this.camTexel.y - this.subTexel.y),
    };
  }
}
